import React, { useState, useEffect } from 'react';
import { t } from '../utils/translations.js';
import { showsService } from '../utils/showsService.js';
import PropTypes from 'prop-types';

const ShowsGrid = () => {
  const [shows, setShows] = useState([]);
  const [filteredShows, setFilteredShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    filterShows();
  }, [activeFilter, searchQuery, shows]);

  useEffect(() => {
    fetchShows();
  }, []);

  const fetchShows = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const showsData = await showsService.getShows();
      const normalizedShows = Array.isArray(showsData) ? showsData : [];
      setShows(normalizedShows);
      setFilteredShows(normalizedShows);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shows:', error);
      setLoadError(t('shows.load_error', 'Unable to load shows right now.'));
      setLoading(false);
    }
  };

  const filterShows = () => {
    let filtered = [...shows];

    // Filter by category
    if (activeFilter !== 'all') {
      filtered = filtered.filter(show => show.category === activeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(show =>
        (show.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (show.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by explicit position first (ascending), then by date as fallback.
    filtered.sort((a, b) => {
      const positionA = Number.isFinite(a.position) ? a.position : null;
      const positionB = Number.isFinite(b.position) ? b.position : null;

      if (positionA !== null && positionB !== null) {
        return positionA - positionB;
      }

      if (positionA !== null) {
        return -1;
      }

      if (positionB !== null) {
        return 1;
      }

      // Try to parse custom date field first
      const dateA = a.date ? new Date(a.date) : new Date(a.publishedAt);
      const dateB = b.date ? new Date(b.date) : new Date(b.publishedAt);
      
      // Handle invalid dates
      const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      
      return timeB - timeA; // Newest first
    });

    setFilteredShows(filtered);
  };

  const filters = [
    { id: 'all', label: t('shows.filter_all', 'All'), count: shows.length },
    { id: 'current', label: t('shows.filter_current', 'Current'), count: shows.filter(s => s.category === 'current').length },
    { id: 'upcoming', label: t('shows.filter_upcoming', 'Upcoming'), count: shows.filter(s => s.category === 'upcoming').length },
    { id: 'archive', label: t('shows.filter_archive', 'Archive'), count: shows.filter(s => s.category === 'archive').length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Em Cartaz':
        return 'var(--shows-status-current, #10b981)';
      case 'Brevemente':
        return 'var(--shows-status-upcoming, #f59e0b)';
      case 'Arquivo':
        return 'var(--shows-status-archive, #64748b)';
      default:
        return 'var(--shows-accent, #5b6cff)';
    }
  };

  const formatDate = (rawDate) => {
    if (!rawDate) return '';
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return rawDate;
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTranslatedStatus = (status) => {
    switch (status) {
      case 'Em Cartaz':
        return t('shows.status_current', 'Current');
      case 'Brevemente':
        return t('shows.status_upcoming', 'Upcoming');
      case 'Arquivo':
        return t('shows.status_archive', 'Archive');
      default:
        return status;
    }
  };

  const getCardImage = (show) => {
    if (show?.featureImage) {
      return show.featureImage;
    }

    const seed = encodeURIComponent(show?.slug || show?.id || show?.title || 'show');
    return `https://picsum.photos/seed/${seed}/900/600`;
  };

  return (
    <div className="shows-grid-react">
      <style>{`
        .shows-grid-react {
          font-family: var(--font-primary, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
          max-width: 1680px;
          margin: 0 auto;
          padding: 2.5rem 2rem 4.5rem;
          min-height: auto;
          background: transparent;
          color: var(--shows-text, #0f172a);
          width: 100%;
        }

        .shows-header {
          text-align: left;
          margin-bottom: 2rem;
          opacity: 0;
          animation: fadeInUp 0.6s ease forwards;
        }

        .shows-title {
          font-family: var(--font-display, 'Inter', sans-serif);
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: var(--shows-text, #0b1220);
          letter-spacing: -0.02em;
          text-wrap: balance;
        }

        .shows-subtitle {
          margin: 0;
          color: var(--shows-text-subtle, #334155);
          font-size: 1.05rem;
          line-height: 1.5;
          max-width: 72ch;
        }

        .shows-controls {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0;
          animation: fadeInUp 0.6s ease 0.2s forwards;
        }

        .shows-search {
          position: relative;
          max-width: 780px;
          margin: 0;
          width: 100%;
        }

        .shows-search-input {
          width: 100%;
          padding: 0.9rem 1.1rem 0.9rem 3rem;
          border: 1px solid var(--shows-border, rgba(15, 23, 42, 0.1));
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: var(--shows-surface-2, #ffffff);
          color: var(--shows-text, #0f172a);
        }

        .shows-search-input::placeholder {
          color: var(--shows-text-subtle, #6b7280);
        }

        .shows-search-input:focus {
          outline: none;
          border-color: var(--shows-accent, #5b6cff);
          box-shadow: 0 0 0 3px var(--shows-muted, rgba(15, 23, 42, 0.08));
        }

        .shows-search-icon {
          position: absolute;
          left: 1.25rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--shows-text-subtle, #6b7280);
          font-size: 1.25rem;
        }

        .shows-filters {
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .filter-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border: 2px solid var(--shows-border, rgba(15, 23, 42, 0.1));
          border-radius: 12px;
          background: var(--shows-surface-2, #ffffff);
          color: var(--shows-text-subtle, #475569);
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s ease;
          -webkit-text-fill-color: currentColor;
          text-shadow: none;
        }

        .filter-label {
          display: inline-block;
          color: inherit;
          font-weight: 700;
          opacity: 1;
          font-size: 0.95rem;
          letter-spacing: 0;
          -webkit-text-fill-color: currentColor;
        }

        .filter-btn:hover {
          border-color: var(--shows-accent, #5b6cff);
          color: var(--shows-accent, #5b6cff);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(91, 108, 255, 0.15);
        }

        .filter-btn.active {
          background: var(--shows-accent, #5b6cff);
          color: var(--shows-on-accent, #ffffff);
          border-color: var(--shows-accent, #5b6cff);
          box-shadow: 0 4px 12px rgba(91, 108, 255, 0.25);
        }

        .filter-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 0.5rem;
          border-radius: 12px;
          background: var(--shows-muted, rgba(15, 23, 42, 0.08));
          color: var(--shows-text, #0f172a);
          font-size: 0.75rem;
          font-weight: 700;
        }

        .filter-btn.active .filter-count {
          background: rgba(255, 255, 255, 0.25);
          color: var(--shows-on-accent, #ffffff);
        }

        /* Guard light-mode filter visibility from global button overrides */
        [data-theme="light"] .shows-grid-react .filter-btn,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #cbd5e1 !important;
          box-shadow: none !important;
          -webkit-text-fill-color: #0f172a !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn *,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn * {
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
          opacity: 1 !important;
          mix-blend-mode: normal !important;
          text-shadow: none !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn:hover,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn:hover {
          background: #eef2ff !important;
          color: #1d4ed8 !important;
          border-color: #93c5fd !important;
          box-shadow: none !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn:hover *,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn:hover * {
          color: #1d4ed8 !important;
          -webkit-text-fill-color: #1d4ed8 !important;
          opacity: 1 !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn.active,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn.active {
          background: #1d4ed8 !important;
          color: #ffffff !important;
          border-color: #1d4ed8 !important;
          box-shadow: none !important;
          -webkit-text-fill-color: #ffffff !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn.active *,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn.active * {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          opacity: 1 !important;
        }

        [data-theme="light"] .shows-grid-react .filter-count,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-count {
          background: #e2e8f0 !important;
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
        }

        [data-theme="light"] .shows-grid-react .filter-btn.active .filter-count,
        :root:not([data-theme="dark"]) .shows-grid-react .filter-btn.active .filter-count {
          background: rgba(255, 255, 255, 0.26) !important;
          color: #ffffff !important;
        }

        .shows-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
          margin-top: 1.75rem;
        }

        .show-card {
          background: var(--shows-card, #ffffff);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--shows-shadow, 0 4px 12px rgba(0, 0, 0, 0.08));
          border: 1px solid var(--shows-border, rgba(15, 23, 42, 0.1));
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          position: relative;
          display: flex;
          flex-direction: column;
          opacity: 0;
          animation: fadeInUp 0.6s ease forwards;
          cursor: pointer;
        }

        .show-card:nth-child(1) { animation-delay: 0.3s; }
        .show-card:nth-child(2) { animation-delay: 0.4s; }
        .show-card:nth-child(3) { animation-delay: 0.5s; }
        .show-card:nth-child(4) { animation-delay: 0.6s; }
        .show-card:nth-child(5) { animation-delay: 0.7s; }
        .show-card:nth-child(6) { animation-delay: 0.8s; }

        .show-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shows-shadow, 0 10px 30px rgba(0, 0, 0, 0.12));
          border-color: var(--shows-accent, #5b6cff);
        }

        .show-card-image-wrapper {
          position: relative;
          height: 220px;
          overflow: hidden;
          background: var(--shows-muted, #f3f4f6);
        }

        .show-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
        }

        .show-card:hover .show-card-image {
          transform: scale(1.05);
        }

        .show-card-status {
          position: absolute;
          top: 0.875rem;
          left: 0.875rem;
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          backdrop-filter: blur(8px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          color: white;
          z-index: 2;
        }

        .show-card-content {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          flex: 1;
        }

        .show-card-title {
          font-family: var(--font-display, 'Inter', sans-serif);
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
          line-height: 1.3;
          color: var(--shows-text, #0b1220);
          letter-spacing: -0.01em;
          transition: color 0.3s ease;
        }

        .show-card:hover .show-card-title {
          color: var(--shows-accent, #5b6cff);
        }

        .show-card-excerpt {
          font-size: 0.95rem;
          color: color-mix(in srgb, var(--shows-text, #0b1220) 72%, white 28%);
          margin: 0;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .show-card-meta {
          margin-top: auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          color: color-mix(in srgb, var(--shows-text, #0b1220) 62%, white 38%);
          font-size: 0.84rem;
          border-top: 1px solid var(--shows-border, rgba(15, 23, 42, 0.1));
          padding-top: 0.75rem;
        }

        .show-card-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .show-results-count {
          text-align: left;
          color: color-mix(in srgb, var(--shows-text, #0b1220) 64%, white 36%);
          font-size: 0.92rem;
          margin-bottom: 0.25rem;
        }

        .shows-error {
          margin: 1rem auto 0;
          max-width: 640px;
          border: 1px solid var(--shows-border, rgba(15, 23, 42, 0.1));
          background: var(--shows-surface-2, #ffffff);
          color: var(--shows-text, #0f172a);
          border-radius: 12px;
          padding: 0.9rem 1rem;
          text-align: center;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 6rem;
        }

        .spinner {
          width: 60px;
          height: 60px;
          border: 4px solid var(--shows-muted, #e5e7eb);
          border-top-color: var(--shows-accent, #5b6cff);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .no-results {
          text-align: left;
          padding: 6rem 2rem;
          color: var(--shows-text-subtle, #6b7280);
        }

        .no-results-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--shows-text, #0b1220);
        }

        [data-theme="dark"] .shows-grid-react .shows-subtitle {
          color: color-mix(in srgb, var(--shows-text, #e7ecf4) 78%, black 22%);
        }

        [data-theme="dark"] .shows-grid-react .show-card-title {
          color: var(--shows-text, #e7ecf4);
        }

        [data-theme="dark"] .shows-grid-react .show-card-excerpt {
          color: color-mix(in srgb, var(--shows-text, #e7ecf4) 72%, black 28%);
        }

        [data-theme="dark"] .shows-grid-react .show-card-meta,
        [data-theme="dark"] .shows-grid-react .show-results-count {
          color: color-mix(in srgb, var(--shows-text, #e7ecf4) 62%, black 38%);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .shows-grid-react {
            padding: 3rem 1.5rem 4rem;
          }

          .shows-header,
          .show-results-count,
          .no-results {
            text-align: center;
          }

          .shows-search {
            margin: 0 auto;
          }

          .shows-filters {
            justify-content: center;
          }

          .shows-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .shows-filters {
            flex-direction: row;
            width: 100%;
            max-width: 100%;
          }

          .filter-btn {
            flex: 1;
            justify-content: center;
            padding: 0.75rem 1rem;
            font-size: 0.875rem;
          }

          .show-card-image-wrapper {
            height: 220px;
          }
        }
      `}</style>

      <div className="shows-header">
        <h1 className="shows-title">{t('shows.title', 'Shows')}</h1>
        <p className="shows-subtitle">{t('shows.subtitle', 'Discover current, upcoming, and archived productions')}</p>
      </div>

      <div className="shows-controls">
        <div className="shows-search">
          <span className="shows-search-icon">🔍</span>
          <input
            type="text"
            className="shows-search-input"
            placeholder={t('shows.search_placeholder', 'Search shows...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="shows-filters">
          {filters.map((filter) => (
            <button
              key={filter.id}
              className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              <span className="filter-label">{filter.label}</span>
              <span className="filter-count">{filter.count}</span>
            </button>
          ))}
        </div>
      </div>

      {!!loadError && <div className="shows-error">{loadError}</div>}

      {!loading && <p className="show-results-count">{filteredShows.length} {t('shows.results_label', 'results')}</p>}

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : filteredShows.length > 0 ? (
        <div className="shows-grid">
          {filteredShows.map((show) => (
            <a 
              key={show.id} 
              href={show.url} 
              className="show-card"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="show-card-image-wrapper">
                <img
                  src={getCardImage(show)}
                  alt={show.title}
                  className="show-card-image"
                  loading="lazy"
                />
                <span
                  className="show-card-status"
                  style={{ backgroundColor: getStatusColor(show.status) }}
                >
                  {getTranslatedStatus(show.status)}
                </span>
              </div>
              <div className="show-card-content">
                <h3 className="show-card-title">{show.title}</h3>
                {(show.date || show.venue) && (
                  <div className="show-card-meta">
                    {show.date && (
                      <span className="show-card-meta-item">
                        <span aria-hidden="true">📅</span>
                        <span>{formatDate(show.date)}</span>
                      </span>
                    )}
                    {show.venue && (
                      <span className="show-card-meta-item">
                        <span aria-hidden="true">📍</span>
                        <span>{show.venue}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <h3 className="no-results-title">{t('shows.no_results_title', 'No results found')}</h3>
          <p>{t('shows.no_results_desc', 'Try adjusting your search or filters')}</p>
        </div>
      )}
    </div>
  );
};

ShowsGrid.propTypes = {
  // Define any prop types if needed
};

export default ShowsGrid;
